import "../adrPdfSectionDataV3/adrPdfTypeDefsV3";

/**
 * Returns # of Wprs that can fit in the remainder of the page.
 * 'remH' param must be provided since there's no binding to
 * the pdf document object (not to be confused with the 'pdf' param
 * here, which is the jsPdf PDF writer.)
 *
 * @param {Object} data - input from madrPdfSectionData
 * @param {number} remH - remaining H (this.maxY - cursorY)
 * @param {number} currRow - current rowIndex in pdfLaborSet
 * @param {Object} pdf
 *
 * @returns {MaxRows}
 */
function maxLaborRowsThisPage(data, remH, currRow, pdf) {
  let maxRows = 0;
  let h = 0;

  if (data.pdfLaborSet.length === 0 || remH < 30)
    return { rawCount: maxRows, lsrCount: 0 };

  let row = currRow;
  do {
    const lsr = data.pdfLaborSet[row];
    const splitFullName = pdf
      .setFont("helvetica", "normal")
      .setFontSize(10)
      .splitTextToSize(lsr.fullName, 114);
    const splitTrade = pdf
      .setFont("helvetica", "normal")
      .setFontSize(10)
      .splitTextToSize(lsr.trade, 69); // 69=250(x_rowInfoRight)-175-6(cell padding)
    if (lsr.otExists || splitFullName.length > 1 || splitTrade.length > 1) {
      // fullName and trade are kept to 2 lines max
      h += 30;
      if (h < remH) maxRows += 2; // Add to maxRows only if h < remH
    } else {
      h += 15;
      if (h < remH) maxRows++;
    }

    if (h < remH) row++;
  } while (h < remH && row < data.pdfLaborSet.length);

  return { rawCount: maxRows, lsrCount: row - currRow };
}

/**
 * Same as {@link maxLaborRowsThisPage}, but for equipment
 *
 * @param {Object} data - input from madrPdfSectionData
 * @param {number} remH - remaining H (this.maxY - cursorY)
 * @param {number} currRow - current rowIndex in equipWpr
 * @param {Object} pdf
 *
 * @returns {MaxRows}
 */
function maxEquipRowsThisPage(data, remH, currRow, pdf) {
  let maxRows = 0;
  let h = 0;

  if (data.pdfEquipmentSet.length === 0 || remH < 30)
    return { rawCount: maxRows, esrCount: 0 };

  let row = currRow;
  do {
    const esr = data.pdfEquipmentSet[row];
    const splitNumberMakeDesc = pdf
      .setFont("helvetica", "normal")
      .setFontSize(10)
      .splitTextToSize(esr.numberMakeDesc, 189);

    if (esr.otExists || splitNumberMakeDesc.length > 1) {
      h += 30;
      if (h < remH) maxRows += 2;
    } else {
      h += 15;
      if (h < remH) maxRows++;
    }

    if (h < remH) row++;
  } while (h < remH && row < data.pdfEquipmentSet.length);

  return { rawCount: maxRows, esrCount: row - currRow };
}

function getSectionH_remarks(data, pdf) {
  let h = 0;

  const remarksCount = data.pdfLaborRemarks.length + data.pdfEquipmentRemarks.length;

  if (remarksCount > 0) {
    h += 2; // Gap between equip and remarks
    h += 15; // Remarks header
    data.pdfLaborRemarks.forEach((lr) => {
      const splitLr = pdf
        .setFont("times", "normal")
        .setFontSize(10)
        .splitTextToSize(lr.remarks, 525);
      h += splitLr.length * 11;
      h += 4;
    });
    data.pdfEquipmentRemarks.forEach((er) => {
      const remarksString = er.remarks
        ? er.operatorData.length > 0
          ? `${er.remarks}\nOperators: ${er.operatorData.join(", ")}`
          : er.remarks
        : er.operatorData.length > 0
        ? `Operators: ${er.operatorData.join(", ")}`
        : "";
      const splitEr = pdf
        .setFont("times", "normal")
        .setFontSize(10)
        .splitTextToSize(remarksString, 525);
      h += splitEr.length * 11;
      h += 4;
    });
  }
  pdf.setFont("helvetica", "normal");

  // console.log("sectionH_remarks=", h);
  return h;
}

/**
 * Render logic for printing 'Hours' section of the Adr.
 * @param {WpHours} data
 */
export default function renderAdrWpHoursV3(data) {
  return function (pdf) {
    // Reusable utility: prints wp column header
    function printWpColHeader(y_lineBelowHdr, cursorY) {
      data.wpSortOrder.forEach((wpSo, index) => {
        const wp = data.pdfWorkPerformed[wpSo.wpTabId];
        const x_wpColLeft = x_hourColLeft + wpHourColWidth * index;

        if (wp.isIdle)
          pdf
            .setFontSize(6)
            .setFont("helvetica", "normal")
            .text("(idle)", x_wpColLeft + 5, y_lineBelowHdr - 9);

        pdf
          .setFontSize(8)
          .setFont("helvetica", "bold")
          .text(wp.wpPrefix, x_wpColLeft + 3, y_lineBelowHdr);
        pdf
          .setFontSize(10)
          .text(wp.wpNumber ? wp.wpNumber.toString() : "-", x_wpColLeft + 3, cursorY);
      });
    }

    let cursorY = this.cursorY;
    // const sectionH_labor = getSectionH_labor(data, pdf); // NOT USED - use maxRows instead
    // const sectionH_equip = getSectionH_equip(data, pdf); // NOT USED - use maxRows instead
    const sectionH_remarks = getSectionH_remarks(data, pdf);

    // console.log("cursorY @ top of wpHours =", cursorY);

    ///////////////////////////////////////////////////////////////////////////
    // Print labor hours (incl. "Hours" header)
    ///////////////////////////////////////////////////////////////////////////

    // Vars for labor /////////////////////////////////////////////////////////
    const x_rowInfoRight = 250; // xPos of vert line between Trade and TimeType
    const x_hourColLeft = x_rowInfoRight + 20; // xPos of vert line right of TimeType

    let y_lineBelowRemarks = 0;
    const wpHourColWidth = 23;
    const x_remarksLabel =
      x_rowInfoRight +
      20 +
      wpHourColWidth * 8 +
      (590 - (x_rowInfoRight + wpHourColWidth * 9)) / 2; // 20 is the width for time type

    // Go to next page if there isn't enough space for "Hours" title, laborWp
    // table header and first row of laborWp table (totaling h=80)
    if (cursorY + 80 > this.maxY) {
      this._addPage(pdf);
      cursorY = this.cursorY;
    }

    ///////////////////////////////////////////////////////////////////////////////////
    // LaborWp master loop: Contains two subloops for 1) printing rows with
    // contractor/name/trade populated, and 2) printing hours.
    // Each iteration of this master loop prints whatever 'slice' of the
    // laborWp data that can fit in the page's remaining space.
    // Each iteration of this master loop outputs one page of data.
    let rowIndex = 0; // Tracks Wpr position at the end of each loop iteration

    // Loop counter. Has 2 purposes: 1) determine labeling of 'cont', and 2) break out of
    // infinite loop if it exceeds a threshold
    let loopCounter = 0;

    do {
      // Section Header // h=19 /////////////////////////////////////////////////////////
      pdf.setFont("helvetica", "bold").setFontSize(12);
      pdf.line(15, cursorY, 590, cursorY);
      cursorY += 2; // ADD 2
      pdf.line(15, cursorY, 590, cursorY);
      cursorY += 13; // ADD 13

      pdf.text(loopCounter === 0 ? "Hours" : "Hours (Continued)", 302, cursorY, {
        align: "center",
      });
      cursorY += 4; // ADD 4
      pdf.line(15, cursorY, 590, cursorY);

      // Table Header for Labor // h=30 /////////////////////////////////////////////////
      const y_lineAboveLaborHdr = cursorY;
      pdf.setFontSize(10);
      cursorY += 11; // ADD 11
      pdf.text("Labor", x_rowInfoRight / 2, cursorY, { align: "center" });
      cursorY += 4; // ADD 4
      pdf.line(15, cursorY, x_rowInfoRight, cursorY);
      const y_lineBelowLaborHdr = cursorY;
      cursorY += 11; // ADD 11
      pdf.text("Contr.", 18, cursorY);
      pdf.text("Name", 58, cursorY);
      pdf.text("Trade", 178, cursorY);

      printWpColHeader(y_lineBelowLaborHdr, cursorY);

      pdf.text("Remarks", x_remarksLabel, cursorY, { align: "center" });

      cursorY += 4; // ADD 4
      pdf.line(15, cursorY, 590, cursorY);

      // Table Body for Labor ///////////////////////////////////////////////////////////
      cursorY += 11; // ADD 11 (currently @bottom of 1st wpr)
      pdf.setFont("helvetica", "normal");

      const remH = this.maxY - cursorY;
      const maxRows = maxLaborRowsThisPage(data, remH, rowIndex, pdf);
      // console.log("remH=", remH);
      // console.log("maxRows=", maxRows);

      if (data.pdfLaborSet.length === 0) {
        // ADD 4
        pdf.text("(N/A)", 18, cursorY); // In Contractor Abbrev
        pdf.text("(N/A)", 58, cursorY); // In Name
        pdf.text("(N/A)", 178, cursorY); // In Trade
        pdf.text("RT", x_rowInfoRight + 3, cursorY);
        cursorY += 4;
        pdf.line(15, cursorY, 590, cursorY);
        rowIndex++;
      } else {
        // console.log("rowIndex=", rowIndex);
        // console.log("total=", data.pdfLaborSet.length);

        /////////////////////////////////////////////////////////////////////////////////
        // Subloop #1: Print pdfLaborSet, horizontal table borders & row data.
        let i = 0; // Declare iterator outside to make its val available outside of loop
        for (i; i < maxRows.lsrCount; i++) {
          const lsr = data.pdfLaborSet[rowIndex + i]; // Initial index: 0+0=0
          const splitFullName = pdf
            .setFont("helvetica", "normal")
            .setFontSize(10)
            .splitTextToSize(lsr.fullName, 114);
          const splitTrade = pdf
            .setFont("helvetica", "normal")
            .setFontSize(10)
            .splitTextToSize(lsr.trade, 69); // 69=250(x_rowInfoRight)-175-6(cell padding)

          pdf.text(lsr.contractorAbbrev, 18, cursorY);

          let cellCursorY = cursorY;
          splitFullName.forEach((sfn, subIndex) => {
            // Keep name to max 2 lines
            if (subIndex < 2) {
              pdf.text(sfn, 58, cellCursorY);
              if (subIndex < splitFullName.length - 1) cellCursorY += 11;
            }
          });
          cellCursorY = cursorY;
          splitTrade.forEach((st, subIndex) => {
            // Keep trade to max 2 lines
            if (subIndex < 2) {
              pdf.text(st, 178, cellCursorY);
              if (subIndex < splitTrade.length - 1) cellCursorY += 11;
            }
          });
          pdf.text("RT", x_rowInfoRight + 3, cursorY);

          if (lsr.remarksKey)
            pdf.text(
              "See " + lsr.remarksKey,
              x_rowInfoRight + 20 + wpHourColWidth * 8 + 3,
              cursorY
            );

          // Print regHours. If Wp is idle, print diagonal.
          Object.entries(lsr.laborHours).forEach(([wpTabId, lh], index) => {
            const wp = data.pdfWorkPerformed[wpTabId];
            if (wp.isIdle)
              pdf.line(
                x_hourColLeft + wpHourColWidth * index,
                cursorY + 4,
                x_hourColLeft + wpHourColWidth * (index + 1),
                cursorY - 11
              );
            else
              pdf.text(
                lh.regHours ? lh.regHours.toString() : "0",
                x_hourColLeft + wpHourColWidth * index + 3,
                cursorY
              );
          });

          cursorY += 4; // cursor @ line between pdfLaborSet's

          // If OT exists, expand the row to fit 2 rows.
          // Word-wrapped expansions also require 2 rows.
          if (lsr.otExists || splitFullName.length > 1 || splitTrade.length > 1) {
            // Horiz line above otHours only extends to the last hour col,
            // and does not extend into the remarks col.
            pdf.line(
              x_rowInfoRight,
              cursorY,
              x_rowInfoRight + wpHourColWidth * 8 + 20,
              cursorY
            );
            cursorY += 11;
            pdf.text("OT", x_rowInfoRight + 3, cursorY);

            // Print otHours. If Wp is idle, print diagonal.
            Object.entries(lsr.laborHours).forEach(([wpTabId, lh], index) => {
              const wp = data.pdfWorkPerformed[wpTabId];
              if (wp.isIdle)
                pdf.line(
                  x_hourColLeft + wpHourColWidth * index,
                  cursorY + 4,
                  x_hourColLeft + wpHourColWidth * (index + 1),
                  cursorY - 11
                );
              else
                pdf.text(
                  lh.otHours ? lh.otHours.toString() : "0",
                  x_hourColLeft + wpHourColWidth * index + 3,
                  cursorY
                );
            });

            cursorY += 4;
          }
          // 19 = 4 to bottom line + 11 to next row's text + 4 to next row's bottom line

          // On the last iteration, draw bottom border
          pdf.line(15, cursorY, 590, cursorY); // Bottom of labor table
          if (i < maxRows.lsrCount - 1) cursorY += 11;
        } // End of Subloop #1: for(i; i < maxRows.lsrCount; i++)
      }

      // At this point, cursorY is at the bottom of labor table, and all pdfLaborSet row data
      // has been printed, along with horiz borders.
      // Now that cursorY is at the bottom of labor table, print all vertical borders:
      pdf.line(55, y_lineBelowLaborHdr, 55, cursorY); // Vert line right of Contr
      pdf.line(175, y_lineBelowLaborHdr, 175, cursorY); // Vert line right of Name
      pdf.line(x_rowInfoRight, y_lineAboveLaborHdr, x_rowInfoRight, cursorY); // Vert line right of Trade

      // Vert lines between laborHours
      for (let i = 0; i < 9; i++) {
        const x_wpColLeft = x_hourColLeft + wpHourColWidth * i;
        pdf.line(x_wpColLeft, y_lineAboveLaborHdr, x_wpColLeft, cursorY);
      }

      // Vert line left of remarks
      pdf.line(x_rowInfoRight + 20, y_lineAboveLaborHdr, x_rowInfoRight + 20, cursorY);

      // Set the rowIndex cursor to the beginning of next page's slice of data
      rowIndex += maxRows.lsrCount;

      // If this is not the last row of pdfLaborSet yet, we must add a new page,
      // and continue the loop.
      if (rowIndex < data.pdfLaborSet.length) {
        this._addPage(pdf);
        cursorY = this.cursorY;
      }

      // console.log("rowIndex @end of loop=", rowIndex);

      // Break out of loop at iterations greater than 100
      if (loopCounter > 100) {
        console.error(
          "Infinite loop encountered at laborWp master loop, rowIndex=",
          rowIndex
        );
        break;
      }
      loopCounter++;
    } while (rowIndex < data.pdfLaborSet.length); // Master laborWp loop

    ///////////////////////////////////////////////////////////////////////////////////
    // EquipWp master loop: Contains two subloops for 1) printing rows with
    // contractor/numberMakeDesc populated, and 2) printing hours.

    // Determines whether to print "Hours (Continued)" for the initial run of the
    // equipWp master loop
    let startEquipWpOnNewPage = false;

    // Go to next page if there isn't enough space for the header block of equipWp
    if (cursorY + 80 > this.maxY) {
      this._addPage(pdf);
      cursorY = this.cursorY;
      startEquipWpOnNewPage = true;
    }

    rowIndex = 0; // See comments above in laborWp
    loopCounter = 0; // See comments above in laborWp

    do {
      // Print section header ONLY if... (otherwise proceed to the table)
      if (startEquipWpOnNewPage || loopCounter > 0) {
        // Section Header // h=19 ///////////////////////////////////////////////////////
        pdf.setFont("helvetica", "bold").setFontSize(12);
        pdf.line(15, cursorY, 590, cursorY);
        cursorY += 2; // ADD 2
        pdf.line(15, cursorY, 590, cursorY);
        cursorY += 13; // ADD 13

        pdf.text("Hours (Continued)", 302, cursorY, { align: "center" });
        cursorY += 4; // ADD 4
        pdf.line(15, cursorY, 590, cursorY);
      }

      // Table Header for Equip // h=30 /////////////////////////////////////////////////
      const y_lineAboveEquipHdr = cursorY;
      pdf.setFontSize(10).setFont("helvetica", "bold");
      cursorY += 11; // ADD 11
      pdf.text("Equipment", x_rowInfoRight / 2, cursorY, { align: "center" });
      cursorY += 4; // ADD 4
      pdf.line(15, cursorY, x_rowInfoRight, cursorY);
      const y_lineBelowEquipHdr = cursorY;
      cursorY += 11; // ADD 11
      pdf.text("Contr.", 18, cursorY);
      pdf.text("Number, Make & Desc.", 58, cursorY);
      // pdf.text("", 178, cursorY);
      printWpColHeader(y_lineBelowEquipHdr, cursorY);
      pdf.text("Remarks", x_remarksLabel, cursorY, { align: "center" });

      cursorY += 4; // ADD 4
      pdf.line(15, cursorY, 590, cursorY);

      // Table Body for Equip ///////////////////////////////////////////////////////////
      cursorY += 11;
      pdf.setFont("helvetica", "normal");

      const remH = this.maxY - cursorY;
      const maxRows = maxEquipRowsThisPage(data, remH, rowIndex, pdf);

      if (data.pdfEquipmentSet.length === 0) {
        pdf.text("(N/A)", 18, cursorY); // In Contractor Abbrev
        pdf.text("(N/A)", 58, cursorY); // In Number, Make & Desc
        pdf.text("RT", x_rowInfoRight + 3, cursorY);
        cursorY += 4;
        pdf.line(15, cursorY, 590, cursorY);
      } else {
        let i = 0;
        for (i; i < maxRows.esrCount; i++) {
          const esr = data.pdfEquipmentSet[rowIndex + i];

          const splitNumberMakeDesc = pdf
            .setFont("helvetica", "normal")
            .setFontSize(10)
            .splitTextToSize(esr.numberMakeDesc, 189);

          pdf.text(esr.contractorAbbrev, 18, cursorY);

          let cellCursorY = cursorY;
          splitNumberMakeDesc.forEach((l, subIndex) => {
            // Keep numberMakeDesc to 2 lines max
            if (subIndex < 2) {
              pdf.text(l, 58, cellCursorY);
              if (subIndex < splitNumberMakeDesc.length - 1) cellCursorY += 11;
            }
          });

          // pdf.text("", 178, cursorY);
          pdf.text("RT", x_rowInfoRight + 3, cursorY);

          // If remarks are found, print
          if (esr.remarksKey)
            pdf.text(
              "See " + esr.remarksKey,
              x_rowInfoRight + 20 + wpHourColWidth * 8 + 3,
              cursorY
            );

          // Print equipHours
          Object.values(esr.equipmentHours).forEach((eh, index) => {
            pdf.text(
              eh.regHours ? eh.regHours.toString() : "0",
              x_hourColLeft + wpHourColWidth * index + 3,
              cursorY
            );
          });

          cursorY += 4;

          // If OT exists, expand the row to fit 2 rows.
          if (esr.otExists || splitNumberMakeDesc.length > 1) {
            // Line above OT only extends to the last hour col,
            // and does not extend into the remarks col.
            pdf.line(
              x_rowInfoRight,
              cursorY,
              x_rowInfoRight + wpHourColWidth * 8 + 20,
              cursorY
            );
            cursorY += 11;
            pdf.text("OT", x_rowInfoRight + 3, cursorY);

            // Print otHours.
            Object.values(esr.equipmentHours).forEach((eh, index) => {
              pdf.text(
                eh.otHours ? eh.otHours.toString() : "0",
                x_hourColLeft + wpHourColWidth * index + 3,
                cursorY
              );
            });
            cursorY += 4;
          }
          // 19 = 4 to bottom line + 11 to next row's text + 4 to next row's bottom line

          pdf.line(15, cursorY, 590, cursorY);
          if (i < maxRows.esrCount - 1) cursorY += 11;
        }
      }

      pdf.line(55, y_lineBelowEquipHdr, 55, cursorY); // Vert line right of Contr
      pdf.line(x_rowInfoRight, y_lineAboveEquipHdr, x_rowInfoRight, cursorY); // Vert line right of Trade

      // Vert lines between equipmenHours
      for (let i = 0; i < 9; i++) {
        const x_wpColLeft = x_hourColLeft + wpHourColWidth * i;
        pdf.line(x_wpColLeft, y_lineAboveEquipHdr, x_wpColLeft, cursorY);
      }

      pdf.line(x_rowInfoRight + 20, y_lineAboveEquipHdr, x_rowInfoRight + 20, cursorY); // Vert line left of remarks

      rowIndex += maxRows.esrCount;

      if (rowIndex < data.pdfEquipmentSet.length) {
        this._addPage(pdf);
        cursorY = this.cursorY;
      }

      if (loopCounter > 100) {
        console.error(
          "Infinite loop encountered at equipWp master loop, rowIndex=",
          rowIndex
        );
        break;
      }
      loopCounter++;
    } while (rowIndex < data.pdfEquipmentSet.length);

    ///////////////////////////////////////////////////////////////////////////
    // Print remarks
    ///////////////////////////////////////////////////////////////////////////
    if (cursorY + sectionH_remarks > this.maxY) {
      this._addPage(pdf);
      cursorY = this.cursorY;
      pdf.setFont("helvetica", "bold").setFontSize(12);
      cursorY += 15;
      pdf.text("Hours (Continued)", 302, cursorY, {
        align: "center",
      });
      cursorY += 4;
    }

    if (data.pdfLaborRemarks.length + data.pdfEquipmentRemarks.length > 0) {
      cursorY += 2;
      pdf.line(15, cursorY, 590, cursorY);
      pdf.setFontSize(10).setFont("helvetica", "bold");
      cursorY += 11;
      pdf.text("Labor & Equipment Remarks", 302, cursorY, {
        align: "center",
      });
      cursorY += 4;
      pdf.line(15, cursorY, 590, cursorY);
      y_lineBelowRemarks = cursorY;
      cursorY += 11; // Title block H = 28

      if (data.pdfLaborRemarks.length > 0) {
        data.pdfLaborRemarks.forEach((lr, index) => {
          const splitLr = pdf
            .setFont("times", "normal")
            .setFontSize(10)
            .splitTextToSize(lr.remarks, 525);
          pdf
            .setFont("helvetica", "normal")
            .text(lr.remarksKey, 18, cursorY)
            .setFont("times", "normal");
          splitLr.forEach((l, index) => {
            pdf.text(l, 58, cursorY);
            if (index < splitLr.length - 1) cursorY += 11;
            else cursorY += 4;
          });
          pdf.line(15, cursorY, 590, cursorY);
          if (index < data.pdfLaborRemarks.length - 1) cursorY += 11;
        });
        pdf.line(55, y_lineBelowRemarks, 55, cursorY); // Right of remarks key
        cursorY += 11;
      }

      if (data.pdfEquipmentRemarks.length > 0) {
        data.pdfEquipmentRemarks.forEach((er, erIndex) => {
          const remarksString = er.remarks
            ? er.operatorData.length > 0
              ? `${er.remarks}\nOperators: ${er.operatorData.join(", ")}`
              : er.remarks
            : er.operatorData.length > 0
            ? `Operators: ${er.operatorData.join(", ")}`
            : "";
          const splitEr = pdf
            .setFont("times", "normal")
            .setFontSize(10)
            .splitTextToSize(remarksString, 525);
          pdf
            .setFont("helvetica", "normal")
            .text(er.remarksKey, 18, cursorY)
            .setFont("times", "normal");
          splitEr.forEach((l, index) => {
            pdf.text(l, 58, cursorY);
            if (index < splitEr.length - 1) cursorY += 11;
            else cursorY += 4;
          });
          pdf.line(15, cursorY, 590, cursorY);
          if (erIndex < data.pdfEquipmentRemarks.length - 1) cursorY += 11;
        });
        pdf.line(55, y_lineBelowRemarks, 55, cursorY); // Right of remarks key
      }
    }

    // console.log("cursorY @ bot of wpHours =", cursorY);
    if (cursorY + 11 < this.maxY) cursorY += 11; // If there's room on the page, place a bottom margin

    this._setDefaults(pdf);
    this.cursorY = cursorY;
  };
}
